// Import Swiper styles
import classNames from 'classnames';
import { MutableRefObject, useEffect, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import ContentNavigation from '@/components/molecules/content-navigation/content-navigation';
import useSwiperState from '@/libraries/hooks/use-swiper-state';
import styles from './carousel-navigation.module.scss';

export interface CarouselNavigationProps {
  /** The class name to apply */
  className?: string;
  /** Whether to show the page count */
  showPageCount?: boolean;
  /** The swiper ref */
  swiperRef: MutableRefObject<SwiperType | undefined>;
}

const CarouselNavigation = ({
  className,
  showPageCount = false,
  swiperRef,
}: CarouselNavigationProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  const { isBeginning, isEnd, isOverflowing } = useSwiperState(swiperRef);

  useEffect(() => {
    const swiper = swiperRef.current;

    if (!swiper) return;

    const handleSlideChange = () => {
      setCurrentIndex(swiper.snapIndex ?? swiper.activeIndex ?? 0);
    };

    const updatePageCount = () => {
      if (!swiper.slides) return;

      const slidesPerView = swiper.params.slidesPerView;

      // When slidesPerView is 'auto', use snapGrid length or total slides
      if (slidesPerView === 'auto') {
        // snapGrid represents the actual stopping points
        const count = swiper.snapGrid?.length || swiper.slides.length;

        setPageCount(count);
      } else {
        const numericSlidesPerView =
          typeof slidesPerView === 'number' ? slidesPerView : 1;

        // Use Math.ceil to handle decimal slidesPerView (e.g., 1.25, 1.5)
        setPageCount(
          Math.max(
            1,
            Math.ceil(swiper.slides.length - numericSlidesPerView + 1),
          ),
        );
      }
    };

    handleSlideChange();
    updatePageCount();

    swiper.on('slideChange', handleSlideChange);
    swiper.on('slideChangeTransitionEnd', handleSlideChange);
    swiper.on('update', updatePageCount);
    swiper.on('snapGridLengthChange', updatePageCount);

    return () => {
      swiper.off('slideChange', handleSlideChange);
      swiper.off('slideChangeTransitionEnd', handleSlideChange);
      swiper.off('update', updatePageCount);
      swiper.off('snapGridLengthChange', updatePageCount);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swiperRef.current]);

  if (!isOverflowing) return null;

  return (
    <div className={classNames(styles.component, className)}>
      <ContentNavigation
        currentIndex={currentIndex}
        disableNext={isEnd}
        disablePrevious={isBeginning}
        onClickNext={() => swiperRef.current?.slideNext()}
        onClickPrevious={() => swiperRef.current?.slidePrev()}
        pageCount={pageCount}
        showPageCount={showPageCount}
      />
    </div>
  );
};

CarouselNavigation.displayName = 'CarouselNavigation';

export default CarouselNavigation;
